import React, { useCallback, useEffect, useRef, useState } from 'react'
import '../../styles/TicketsPage.css'
import UserManagementPage from './UserManagementPage'
import { CUSTOMER_TYPES } from '../../constants/ticketFormOptions'

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100]

function buildPageNumbers(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)
  const pages = [1]
  if (current > 3) pages.push('...')
  for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) pages.push(i)
  if (current < total - 2) pages.push('...')
  pages.push(total)
  return pages
}

const TABS = [
  { id: 'issue_management',        label: 'Issue Management' },
  { id: 'user_access',             label: 'User Access' },
  { id: 'coordinator_management',  label: 'Coordinator Management' },
]

const COLUMNS = [
  { label: '#',                  bg: null,      color: null },
  { label: 'Issue Category',     bg: null,      color: null },
  { label: 'Sub-Category',       bg: null,      color: null },
  { label: 'Region',             bg: null,      color: null },
  { label: 'Type of Customer',   bg: null,      color: null },
  { label: 'L1 Name',        bg: '#DBEAFE', color: '#1D4ED8' },
  { label: 'L1 Email',       bg: '#DBEAFE', color: '#1D4ED8' },
  { label: 'L1 Mobile',      bg: '#DBEAFE', color: '#1D4ED8' },
  { label: 'L1 TAT (min)',   bg: '#DBEAFE', color: '#1D4ED8' },
  { label: 'L2 Name',        bg: '#EDE9FE', color: '#6D28D9' },
  { label: 'L2 Email',       bg: '#EDE9FE', color: '#6D28D9' },
  { label: 'L2 Mobile',      bg: '#EDE9FE', color: '#6D28D9' },
  { label: 'L2 TAT (min)',   bg: '#EDE9FE', color: '#6D28D9' },
  { label: 'L3 Name',        bg: '#DCFCE7', color: '#15803D' },
  { label: 'L3 Email',       bg: '#DCFCE7', color: '#15803D' },
  { label: 'L3 Mobile',      bg: '#DCFCE7', color: '#15803D' },
  { label: 'L3 TAT (min)',   bg: '#DCFCE7', color: '#15803D' },
  { label: 'L4 Name',        bg: '#FEF3C7', color: '#B45309' },
  { label: 'L4 Email',       bg: '#FEF3C7', color: '#B45309' },
  { label: 'L4 Mobile',      bg: '#FEF3C7', color: '#B45309' },
  { label: 'L4 TAT (min)',   bg: '#FEF3C7', color: '#B45309' },
]

const Cell = ({ value }) => (
  <td style={{ padding: '10px 14px', borderBottom: '1px solid #F3F4F6', fontSize: 13, color: '#374151', whiteSpace: 'nowrap' }}>
    {value || <span style={{ color: '#D1D5DB' }}>—</span>}
  </td>
)

/* ─── helpers ─────────────────────────────────────────── */
const inputStyle = {
  width: '100%', padding: '8px 10px', fontSize: 13,
  border: '1px solid #D1D5DB', borderRadius: 7,
  outline: 'none', color: '#111827', background: '#fff',
  boxSizing: 'border-box',
}
const labelStyle = { fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4, display: 'block' }

const Field = ({ label, required, children }) => (
  <div style={{ display: 'flex', flexDirection: 'column' }}>
    <label style={labelStyle}>
      {label}{required && <span style={{ color: '#EF4444' }}> *</span>}
    </label>
    {children}
  </div>
)

const emptyCoord = () => ({ name: '', phone: '+91', email: '', tat: '' })

const isValidPhone = (ph) => {
  const digits = ph.replace(/^\+91/, '').replace(/\D/g, '')
  return digits.length === 10
}

const COORD_LEVELS = [
  { key: 'l1_coordinator', label: 'L1 Coordinator', required: false },
  { key: 'l2_coordinator', label: 'L2 Coordinator', required: false },
  { key: 'l3_coordinator', label: 'L3 Manager',     required: false },
  { key: 'l4_coordinator', label: 'L4 Manager',     required: false },
]

/* ─── Category Search Dropdown ────────────────────────── */
const CategorySearchDropdown = ({ fetchJson, value, onSelect, placeholder, disabled, apiParam }) => {
  const [open, setOpen]       = useState(false)
  const [items, setItems]     = useState([])
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [page, setPage]       = useState(1)
  const [search, setSearch]   = useState('')
  const loadingRef            = useRef(false)
  const listRef               = useRef(null)
  const wrapRef               = useRef(null)

  /* pre-fill search with current value when opening so user can edit it */
  const handleOpen = () => {
    if (disabled) return
    setSearch(value || '')
    setOpen(o => !o)
  }

  const load = useCallback(async (pg = 1, append = false) => {
    if (!fetchJson || loadingRef.current) return
    loadingRef.current = true
    setLoading(true)
    try {
      const params = new URLSearchParams({ page: String(pg) })
      if (apiParam) Object.entries(apiParam).forEach(([k, v]) => params.set(k, v))
      const data = await fetchJson(`/api/categories/category?${params}`)
      const results = Array.isArray(data) ? data : (data?.results || data?.data || [])
      setItems(prev => append ? [...prev, ...results] : results)
      setHasMore(!!data?.next)
      setPage(pg)
    } catch { if (!append) setItems([]) }
    finally { loadingRef.current = false; setLoading(false) }
  }, [fetchJson, apiParam ? JSON.stringify(apiParam) : ''])

  useEffect(() => {
    if (open) load(1, false)
  }, [open])

  useEffect(() => {
    if (!open) return
    const handler = e => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const handleScroll = () => {
    const el = listRef.current
    if (!el || !hasMore || loading) return
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 60) load(page + 1, true)
  }

  const filtered = search.trim()
    ? items.filter(i => i.name.toLowerCase().includes(search.toLowerCase()))
    : items

  const exactMatch = items.some(i => i.name.toLowerCase() === search.trim().toLowerCase())

  const dropStyle = {
    border: `1px solid ${open ? '#6366F1' : '#D1D5DB'}`, borderRadius: 7,
    padding: '8px 10px', cursor: disabled ? 'not-allowed' : 'pointer',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    background: disabled ? '#F9FAFB' : '#fff', fontSize: 13, minHeight: 37,
    color: value ? '#111827' : '#9CA3AF',
  }

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <div style={dropStyle} onClick={handleOpen}>
        <span>{value || placeholder}</span>
        <span style={{ color: '#9CA3AF', fontSize: 10 }}>▼</span>
      </div>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200,
          background: '#fff', border: '1px solid #E0E7EF', borderRadius: 8,
          boxShadow: '0 6px 18px rgba(0,0,0,.12)', marginTop: 4,
        }}>
          {/* Search input */}
          <div style={{ padding: '8px 10px', borderBottom: '1px solid #F1F5F9' }}>
            <input
              autoFocus
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search or type new name…"
              style={{ width: '100%', border: 'none', outline: 'none', fontSize: 13, color: '#111827', boxSizing: 'border-box' }}
            />
          </div>
          {/* List */}
          <div ref={listRef} onScroll={handleScroll} style={{ maxHeight: 200, overflowY: 'auto' }}>
            {filtered.map(item => (
              <div
                key={item.id || item.slug}
                onClick={() => { onSelect(item.name, item.slug, item.id); setSearch(''); setOpen(false) }}
                style={{
                  padding: '8px 12px', cursor: 'pointer', fontSize: 13,
                  color: '#111827', borderBottom: '1px solid #F9FAFB',
                  background: value === item.name ? '#EFF6FF' : undefined,
                }}
                onMouseEnter={e => e.currentTarget.style.background = value === item.name ? '#DBEAFE' : '#F8FAFB'}
                onMouseLeave={e => e.currentTarget.style.background = value === item.name ? '#EFF6FF' : '#fff'}
              >
                {item.name}
              </div>
            ))}
            {loading && <div style={{ padding: '8px 12px', fontSize: 12, color: '#9CA3AF', textAlign: 'center' }}>Loading…</div>}
            {!loading && filtered.length === 0 && !search.trim() && (
              <div style={{ padding: '8px 12px', fontSize: 12, color: '#9CA3AF', textAlign: 'center' }}>No items found</div>
            )}
            {hasMore && !loading && (
              <div style={{ padding: '6px 12px', fontSize: 11, color: '#B0B8C4', textAlign: 'center' }}>↓ Scroll to load more</div>
            )}
            {/* Create new option */}
            {search.trim() && !exactMatch && (
              <div
                onClick={() => { onSelect(search.trim(), '', ''); setSearch(''); setOpen(false) }}
                style={{
                  padding: '8px 12px', cursor: 'pointer', fontSize: 13,
                  color: '#2563EB', borderTop: '1px solid #F1F5F9',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}
                onMouseEnter={e => e.currentTarget.style.background = '#EFF6FF'}
                onMouseLeave={e => e.currentTarget.style.background = '#fff'}
              >
                <span style={{ fontWeight: 700, fontSize: 15, lineHeight: 1 }}>+</span>
                Create "{search.trim()}"
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

const rowToForm = (row) => ({
  categoryName:    row?.category?.name     || '',
  categorySlug:    row?.category?.slug     || '',
  subCategoryName: row?.sub_category?.name || '',
  subCategorySlug: row?.sub_category?.slug || '',
  regionSlug:      row?.region?.slug       || '',
  typeOfCustomer:  row?.type_of_customer   || '',
  l1_coordinator: row?.l1_coordinator
    ? { ...row.l1_coordinator, phone: row.l1_coordinator.phone || '+91', tat: row.l1_coordinator.tat ?? '' }
    : emptyCoord(),
  l2_coordinator: row?.l2_coordinator
    ? { ...row.l2_coordinator, phone: row.l2_coordinator.phone || '+91', tat: row.l2_coordinator.tat ?? '' }
    : null,
  l3_coordinator: row?.l3_coordinator
    ? { ...row.l3_coordinator, phone: row.l3_coordinator.phone || '+91', tat: row.l3_coordinator.tat ?? '' }
    : null,
  l4_coordinator: row?.l4_coordinator
    ? { ...row.l4_coordinator, phone: row.l4_coordinator.phone || '+91', tat: row.l4_coordinator.tat ?? '' }
    : null,
})

const emptyForm = () => ({
  categoryName: '', categorySlug: '',
  subCategoryName: '', subCategorySlug: '',
  regionSlug: '',
  typeOfCustomer: '',
  l1_coordinator: emptyCoord(),
  l2_coordinator: null,
  l3_coordinator: null,
  l4_coordinator: null,
})

/* ─── Category Drawer (Add + Edit) ────────────────────── */
const CategoryDrawer = ({ open, onClose, fetchJson, onSuccess, editRow, onDelete }) => {
  const isEdit = !!editRow
  const [regions, setRegions]               = useState([])
  const [regionsLoading, setRegionsLoading] = useState(false)
  const [submitting, setSubmitting]         = useState(false)
  const [toast, setToast]                   = useState(null)
  const toastTimer                          = useRef(null)

  const [form, setForm] = useState(emptyForm)

  /* fetch regions when drawer opens */
  useEffect(() => {
    if (!open || !fetchJson) return
    setRegionsLoading(true)
    fetchJson('/api/geographies/geography/regions')
      .then(data => setRegions(Array.isArray(data) ? data : (data?.data || data?.results || [])))
      .catch(() => {})
      .finally(() => setRegionsLoading(false))
  }, [open, fetchJson])

  /* reset / pre-fill on open */
  useEffect(() => {
    if (open) {
      setForm(isEdit ? rowToForm(editRow) : emptyForm())
      setToast(null)
    }
  }, [open])

  const showToast = (ok, msg) => {
    clearTimeout(toastTimer.current)
    setToast({ ok, msg })
    toastTimer.current = setTimeout(() => setToast(null), 4000)
  }

  const setCoord = (key, field, value) => {
    setForm(f => ({ ...f, [key]: { ...(f[key] || emptyCoord()), [field]: value } }))
  }

  const toggleCoord = (key, enabled) => {
    setForm(f => ({ ...f, [key]: enabled ? emptyCoord() : null }))
  }

  const handleSubmit = async () => {
    if (!form.categoryName.trim())    return showToast(false, 'Category name is required.')
    if (!form.subCategoryName.trim()) return showToast(false, 'Sub-category name is required.')
    if (!form.regionSlug)             return showToast(false, 'Please select a region.')

    for (const { key, label } of COORD_LEVELS) {
      const c = form[key]
      if (!c) continue
      if (!c.name.trim())  return showToast(false, `${label}: Name is required.`)
      if (!c.email.trim()) return showToast(false, `${label}: Email is required.`)
      if (c.phone && c.phone !== '+91' && !isValidPhone(c.phone))
        return showToast(false, `${label}: Enter a valid 10-digit phone number.`)
      if (c.tat === '' || c.tat === null || c.tat === undefined)
        return showToast(false, `${label}: TAT (minutes) is required.`)
    }

    const coordPayload = (key) => {
      const c = form[key]
      if (!c) return null
      if (!c.name.trim() && !c.phone.trim() && !c.email.trim()) return null
      const phone = c.phone && c.phone !== '+91' ? c.phone : null
      const tat = c.tat !== '' && c.tat !== null && c.tat !== undefined ? Number(c.tat) : null
      return { name: c.name, phone, email: c.email, tat }
    }

    const body = {
      categories: {
        category:          { name: form.categoryName.trim() },
        sub_category:      { name: form.subCategoryName.trim() },
        region:            { slug: form.regionSlug },
        type_of_customer:  form.typeOfCustomer.trim() || null,
        l1_coordinator:    coordPayload('l1_coordinator'),
        l2_coordinator:    coordPayload('l2_coordinator'),
        l3_coordinator:    coordPayload('l3_coordinator'),
        l4_coordinator:    coordPayload('l4_coordinator'),
      }
    }

    setSubmitting(true)
    try {
      if (isEdit) {
        await fetchJson(`/api/categories/admin-category/${editRow.issue_category_id}/`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        })
        showToast(true, 'Category updated successfully.')
      } else {
        await fetchJson('/api/categories/admin-category/', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        })
        showToast(true, 'Category added successfully.')
      }
      onSuccess()
      setTimeout(onClose, 1200)
    } catch (err) {
      showToast(false, err?.message || (isEdit ? 'Failed to update category.' : 'Failed to add category.'))
    } finally {
      setSubmitting(false)
    }
  }

  if (!open) return null

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,.35)', zIndex: 900,
        }}
      />
      {/* Drawer */}
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, zIndex: 901,
        width: 480, background: '#fff',
        boxShadow: '-4px 0 24px rgba(0,0,0,.15)',
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          padding: '18px 20px', borderBottom: '1px solid #E5E7EB',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexShrink: 0, background: '#fff',
        }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>{isEdit ? 'Edit Category' : 'Add Category'}</div>
            <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>{isEdit ? 'Update the issue category details' : 'Fill in the details to create a new issue category'}</div>
          </div>
          <button onClick={onClose} style={{
            background: '#F3F4F6', border: 'none', borderRadius: 6,
            width: 28, height: 28, cursor: 'pointer', fontSize: 16,
            display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6B7280',
          }}>×</button>
        </div>

        {/* Toast — outside scroll so always visible */}
        {toast && (
          <div style={{
            flexShrink: 0, margin: '10px 20px 0', padding: '10px 14px', borderRadius: 8, fontSize: 13,
            background: toast.ok ? '#DCFCE7' : '#FEE2E2',
            color: toast.ok ? '#166534' : '#991B1B',
            border: `1px solid ${toast.ok ? '#BBF7D0' : '#FECACA'}`,
          }}>
            {toast.msg}
          </div>
        )}

        {/* Body — only this section scrolls */}
        <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: 20, flex: 1, overflowY: 'auto' }}>

          {/* Basic Info */}
          <Section title="Basic Info">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="Category Name" required>
                <CategorySearchDropdown
                  fetchJson={fetchJson}
                  value={form.categoryName}
                  placeholder="Search or create category…"
                  onSelect={(name, slug) => setForm(f => ({
                    ...f,
                    categoryName: name, categorySlug: slug || '',
                    subCategoryName: '', subCategorySlug: '',
                  }))}
                />
              </Field>
              <Field label="Sub-Category Name" required>
                <CategorySearchDropdown
                  fetchJson={fetchJson}
                  value={form.subCategoryName}
                  placeholder={form.categoryName ? 'Search or create sub-category…' : 'Select category first'}
                  disabled={!form.categoryName}
                  apiParam={form.categoryName ? { category: form.categorySlug || form.categoryName } : null}
                  onSelect={(name, slug) => setForm(f => ({ ...f, subCategoryName: name, subCategorySlug: slug }))}
                />
              </Field>
            </div>
            <Field label="Region" required>
              <select
                style={inputStyle} value={form.regionSlug}
                onChange={e => setForm(f => ({ ...f, regionSlug: e.target.value }))}
              >
                <option value="">{regionsLoading ? 'Loading…' : 'Select region…'}</option>
                {regions.map(r => (
                  <option key={r.id} value={r.slug}>{r.name}</option>
                ))}
              </select>
            </Field>
            <Field label="Type of Customer (if empty, applies to all customer types)">
              <select
                style={inputStyle}
                value={form.typeOfCustomer}
                onChange={e => setForm(f => ({ ...f, typeOfCustomer: e.target.value }))}
              >
                <option value="">All customer types (optional)</option>
                {CUSTOMER_TYPES.map(ct => (
                  <option key={ct.value} value={ct.value}>{ct.label}</option>
                ))}
              </select>
            </Field>
          </Section>

          {/* Coordinator Sections */}
          {COORD_LEVELS.map(({ key, label }) => (
            <CoordinatorSection
              key={key}
              label={label}
              value={form[key]}
              onChange={(field, val) => setCoord(key, field, val)}
              onToggle={enabled => toggleCoord(key, enabled)}
              fetchJson={fetchJson}
            />
          ))}
        </div>

        {/* Footer */}
        <div style={{
          padding: '14px 20px', borderTop: '1px solid #E5E7EB',
          display: 'flex', gap: 10, alignItems: 'center',
          flexShrink: 0, background: '#fff',
        }}>
          {isEdit && onDelete && (
            <button
              onClick={onDelete}
              style={{
                padding: '8px 18px', borderRadius: 8, border: '1px solid #FECACA',
                background: '#FEF2F2', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#EF4444',
                display: 'flex', alignItems: 'center', gap: 6,
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#FEE2E2'}
              onMouseLeave={e => e.currentTarget.style.background = '#FEF2F2'}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
                <path d="M10 11v6"/><path d="M14 11v6"/>
                <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
              </svg>
              Delete
            </button>
          )}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 10 }}>
            <button
              onClick={onClose}
              style={{
                padding: '8px 20px', borderRadius: 8, border: '1px solid #D1D5DB',
                background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#374151',
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              style={{
                padding: '8px 24px', borderRadius: 8, border: 'none',
                background: submitting ? '#93C5FD' : '#2563EB',
                cursor: submitting ? 'not-allowed' : 'pointer',
                fontSize: 13, fontWeight: 600, color: '#fff',
              }}
            >
              {submitting ? (isEdit ? 'Updating…' : 'Saving…') : (isEdit ? 'Update Category' : 'Save Category')}
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

/* ─── Section wrapper ──────────────────────────────────── */
const Section = ({ title, children }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
    <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', paddingBottom: 6, borderBottom: '1px solid #F3F4F6' }}>
      {title}
    </div>
    {children}
  </div>
)

/* ─── Toggle switch ────────────────────────────────────── */
const Toggle = ({ value, onChange }) => (
  <button
    type="button"
    onClick={() => onChange(!value)}
    style={{
      width: 40, height: 22, borderRadius: 11, border: 'none',
      background: value ? '#2563EB' : '#D1D5DB',
      cursor: 'pointer', position: 'relative', flexShrink: 0,
      transition: 'background .2s',
    }}
  >
    <span style={{
      position: 'absolute', top: 3, left: value ? 20 : 3,
      width: 16, height: 16, borderRadius: '50%', background: '#fff',
      transition: 'left .2s', boxShadow: '0 1px 3px rgba(0,0,0,.2)',
    }} />
  </button>
)

/* ─── Agent Search ─────────────────────────────────────── */
const AgentSearchInput = ({ fetchJson, onSelect }) => {
  const [query, setQuery]     = useState('')
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(false)
  const [open, setOpen]       = useState(false)
  const wrapRef               = useRef(null)
  const debounceRef           = useRef(null)

  useEffect(() => {
    if (!open) return
    const handler = e => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const handleChange = e => {
    const q = e.target.value
    setQuery(q)
    clearTimeout(debounceRef.current)
    if (!q.trim()) { setResults([]); setOpen(false); return }
    debounceRef.current = setTimeout(async () => {
      if (!fetchJson) return
      setLoading(true)
      try {
        const params = new URLSearchParams({ q })
        const data = await fetchJson(`/api/agents/agent?${params}`)
        const list = Array.isArray(data) ? data : (data?.results || data?.data || [])
        setResults(list)
        setOpen(true)
      } catch { setResults([]) }
      finally { setLoading(false) }
    }, 300)
  }

  const handleSelect = agent => {
    onSelect(agent)
    setQuery('')
    setResults([])
    setOpen(false)
  }

  return (
    <div ref={wrapRef} style={{ position: 'relative', gridColumn: '1 / -1' }}>
      <label style={{ ...labelStyle, marginBottom: 4 }}>Search Agent</label>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        border: '1px solid #D1D5DB', borderRadius: 7,
        padding: '8px 10px', background: '#fff',
      }}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
        </svg>
        <input
          value={query}
          onChange={handleChange}
          placeholder="Type name or code to search…"
          style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 13, color: '#111827', width: '100%' }}
        />
        {loading && <span style={{ fontSize: 11, color: '#9CA3AF', whiteSpace: 'nowrap' }}>Searching…</span>}
      </div>
      {open && results.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 300,
          background: '#fff', border: '1px solid #E0E7EF', borderRadius: 8,
          boxShadow: '0 6px 18px rgba(0,0,0,.12)', marginTop: 4, maxHeight: 200, overflowY: 'auto',
        }}>
          {results.map(agent => (
            <div
              key={agent.id}
              onClick={() => handleSelect(agent)}
              style={{ padding: '10px 12px', cursor: 'pointer', borderBottom: '1px solid #F3F4F6' }}
              onMouseEnter={e => e.currentTarget.style.background = '#F9FAFB'}
              onMouseLeave={e => e.currentTarget.style.background = '#fff'}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{agent.name}</div>
                {agent.agent_code && <span style={{ fontSize: 11, fontWeight: 600, color: '#6366F1', background: '#EEF2FF', borderRadius: 4, padding: '2px 6px' }}>{agent.agent_code}</span>}
              </div>
              <div style={{ fontSize: 11, color: '#6B7280', marginTop: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {agent.email  && <span>✉ {agent.email}</span>}
                {agent.mobile && <span>📱 {agent.mobile}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
      {open && !loading && results.length === 0 && query.trim() && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 300,
          background: '#fff', border: '1px solid #E0E7EF', borderRadius: 8,
          boxShadow: '0 6px 18px rgba(0,0,0,.12)', marginTop: 4,
          padding: '10px 12px', fontSize: 13, color: '#9CA3AF', textAlign: 'center',
        }}>
          No agents found
        </div>
      )}
    </div>
  )
}

/* ─── Coordinator Section ──────────────────────────────── */
const CoordinatorSection = ({ label, value, onChange, onToggle, fetchJson }) => {
  const enabled = value !== null
  return (
    <Section title={
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span>{label}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 11, color: '#6B7280', fontWeight: 400 }}>{enabled ? 'Enabled' : 'Disabled'}</span>
          <Toggle value={enabled} onChange={onToggle} />
        </div>
      </div>
    }>
      {enabled && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <AgentSearchInput
            fetchJson={fetchJson}
            onSelect={agent => {
              onChange('name',  agent.name  || '')
              onChange('email', agent.email || '')
              onChange('phone', agent.mobile || '+91')
            }}
          />
          <Field label="Name" required>
            <input style={inputStyle} value={value.name} onChange={e => onChange('name', e.target.value)} placeholder="Full name" />
          </Field>
          <Field label="Phone">
            <input
              style={{
                ...inputStyle,
                borderColor: value.phone && value.phone !== '+91' && !isValidPhone(value.phone) ? '#EF4444' : undefined,
              }}
              value={value.phone}
              onChange={e => {
                let v = e.target.value
                if (!v.startsWith('+91')) v = '+91' + v.replace(/^\+91?/, '')
                const prefix = '+91'
                const digits = v.slice(prefix.length).replace(/\D/g, '').slice(0, 10)
                onChange('phone', prefix + digits)
              }}
              placeholder="+91XXXXXXXXXX"
            />
          </Field>
          <Field label="Email" required>
            <input style={inputStyle} value={value.email} onChange={e => onChange('email', e.target.value)} placeholder="email@example.com" type="email" />
          </Field>
          <Field label="TAT (minutes)" required>
            <input
              style={inputStyle}
              type="number"
              min="0"
              value={value.tat ?? ''}
              onChange={e => onChange('tat', e.target.value)}
              placeholder="e.g. 60"
            />
          </Field>
        </div>
      )}
    </Section>
  )
}

/* ─── Issue Management Tab ─────────────────────────────── */
const IssueManagementTab = ({ fetchJson }) => {
  const [rows, setRows]             = useState([])
  const [loading, setLoading]       = useState(false)
  const [totalCount, setTotalCount] = useState(0)
  const [page, setPage]             = useState(1)
  const [pageSize, setPageSize]     = useState(10)
  const [search, setSearch]         = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editRow, setEditRow]       = useState(null)
  const [deleteConfirmRow, setDeleteConfirmRow] = useState(null)
  const [deleting, setDeleting]     = useState(false)
  const searchDebounce              = useRef(null)
  const [refreshKey, setRefreshKey] = useState(0)

  useEffect(() => {
    clearTimeout(searchDebounce.current)
    searchDebounce.current = setTimeout(() => { setDebouncedSearch(search); setPage(1) }, 350)
    return () => clearTimeout(searchDebounce.current)
  }, [search])

  useEffect(() => { setPage(1) }, [pageSize])

  useEffect(() => {
    if (!fetchJson) return
    setLoading(true)
    const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) })
    if (debouncedSearch) params.set('search', debouncedSearch)
    fetchJson(`/api/categories/admin-category/?${params}`)
      .then(data => {
        const results = data?.results || []
        setRows(results)
        setTotalCount(data?.count ?? results.length)
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [fetchJson, page, pageSize, debouncedSearch, refreshKey])

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize))

  const handleDelete = async () => {
    if (!deleteConfirmRow) return
    setDeleting(true)
    try {
      await fetchJson(`/api/categories/admin-category/${deleteConfirmRow.issue_category_id}/`, { method: 'DELETE' })
      setDeleteConfirmRow(null)
      setRefreshKey(k => k + 1)
      if (rows.length === 1 && page > 1) setPage(p => p - 1)
    } catch (err) {
      alert(err?.message || 'Failed to delete category.')
    } finally {
      setDeleting(false)
    }
  }

  return (
    <>
      {/* Add drawer */}
      <CategoryDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        fetchJson={fetchJson}
        onSuccess={() => { setRefreshKey(k => k + 1); setPage(1) }}
        editRow={null}
      />
      {/* Edit drawer */}
      <CategoryDrawer
        open={!!editRow}
        onClose={() => setEditRow(null)}
        fetchJson={fetchJson}
        onSuccess={() => { setRefreshKey(k => k + 1) }}
        editRow={editRow}
        onDelete={() => { const row = editRow; setEditRow(null); setDeleteConfirmRow(row) }}
      />
      {/* Delete confirmation modal */}
      {deleteConfirmRow && (
        <>
          <div onClick={() => setDeleteConfirmRow(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', zIndex: 1000 }} />
          <div style={{
            position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
            zIndex: 1001, background: '#fff', borderRadius: 12,
            boxShadow: '0 8px 32px rgba(0,0,0,.18)', padding: '28px 28px 22px', width: 360, maxWidth: '90vw',
          }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#111827', marginBottom: 8 }}>Delete Category?</div>
            <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 20, lineHeight: 1.5 }}>
              This will permanently delete <strong>{deleteConfirmRow.category?.name} / {deleteConfirmRow.sub_category?.name}</strong>. This action cannot be undone.
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setDeleteConfirmRow(null)}
                style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #D1D5DB', background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#374151' }}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: deleting ? '#FCA5A5' : '#EF4444', cursor: deleting ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600, color: '#fff' }}
              >
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Toolbar */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            background: '#F9FAFB', border: '1px solid #E5E7EB',
            borderRadius: 8, padding: '7px 12px', width: 300,
          }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search categories..."
              style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 13, color: '#374151', width: '100%' }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {totalCount > 0 && (
              <span style={{ fontSize: 13, color: '#6B7280' }}>{totalCount} records</span>
            )}
            <button
              onClick={() => setDrawerOpen(true)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                background: '#2563EB', color: '#fff', border: 'none',
                borderRadius: 8, padding: '8px 16px', cursor: 'pointer',
                fontSize: 13, fontWeight: 600,
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#1D4ED8'}
              onMouseLeave={e => e.currentTarget.style.background = '#2563EB'}
            >
              <span style={{ fontSize: 16, lineHeight: 1 }}>+</span>
              Add Category
            </button>
          </div>
        </div>

        {/* Table */}
        <div style={{ border: '1px solid #E5E7EB', borderRadius: 10, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
                {COLUMNS.map(col => (
                  <th key={col.label} style={{
                    padding: '10px 14px', textAlign: 'left',
                    fontSize: 11, fontWeight: 700,
                    color: col.color || '#6B7280',
                    background: col.bg || '#F9FAFB',
                    textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap',
                  }}>
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={COLUMNS.length} style={{ padding: '48px 0', textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={COLUMNS.length} style={{ padding: '48px 0', textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>No categories found</td></tr>
              ) : rows.map((row, idx) => (
                <tr key={row.issue_category_id}
                  onClick={() => setEditRow(row)}
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#F0F7FF'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}
                >
                  <td style={{ padding: '10px 14px', borderBottom: '1px solid #F3F4F6', fontSize: 13, color: '#9CA3AF', whiteSpace: 'nowrap' }}>
                    {(page - 1) * pageSize + idx + 1}
                  </td>
                  <Cell value={row.category?.name} />
                  <Cell value={row.sub_category?.name} />
                  <Cell value={row.region?.name} />
                  <Cell value={row.type_of_customer} />
                  <Cell value={row.l1_coordinator?.name} />
                  <Cell value={row.l1_coordinator?.email} />
                  <Cell value={row.l1_coordinator?.phone} />
                  <Cell value={row.l1_coordinator?.tat != null ? String(row.l1_coordinator.tat) : null} />
                  <Cell value={row.l2_coordinator?.name} />
                  <Cell value={row.l2_coordinator?.email} />
                  <Cell value={row.l2_coordinator?.phone} />
                  <Cell value={row.l2_coordinator?.tat != null ? String(row.l2_coordinator.tat) : null} />
                  <Cell value={row.l3_coordinator?.name} />
                  <Cell value={row.l3_coordinator?.email} />
                  <Cell value={row.l3_coordinator?.phone} />
                  <Cell value={row.l3_coordinator?.tat != null ? String(row.l3_coordinator.tat) : null} />
                  <Cell value={row.l4_coordinator?.name} />
                  <Cell value={row.l4_coordinator?.email} />
                  <Cell value={row.l4_coordinator?.phone} />
                  <Cell value={row.l4_coordinator?.tat != null ? String(row.l4_coordinator.tat) : null} />
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="t360-pagination">
          {totalPages > 1 && (
            <>
              <button className="t360-page-btn" onClick={() => setPage(p => p - 1)} disabled={page === 1 || loading}>← Prev</button>
              {buildPageNumbers(page, totalPages).map((p, i) =>
                p === '...' ? (
                  <span key={`el-${i}`} className="t360-page-ellipsis">…</span>
                ) : (
                  <button key={p} className={`t360-page-btn${p === page ? ' active' : ''}`} onClick={() => setPage(p)} disabled={loading}>{p}</button>
                )
              )}
              <button className="t360-page-btn" onClick={() => setPage(p => p + 1)} disabled={page === totalPages || loading}>Next →</button>
              <span className="t360-page-info">Page {page} of {totalPages}</span>
            </>
          )}
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 12, color: '#6B7280', whiteSpace: 'nowrap' }}>Rows per page:</span>
            <div className="t360-select-wrap" style={{ minWidth: 70 }}>
              <select className="t360-select" value={pageSize} onChange={e => setPageSize(Number(e.target.value))} disabled={loading}>
                {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
              <span className="t360-select-arrow">▼</span>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

/* ─── Avatar helpers (local, same logic as UserManagementPage) ── */
function coordAvatarColor(name) {
  const colors = ['#16A34A','#0284C7','#7C3AED','#DC2626','#EA580C','#D97706','#0F766E','#BE185D']
  let h = 0
  for (let i = 0; i < (name || '').length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffffffff
  return colors[Math.abs(h) % colors.length]
}
function coordInitials(name) {
  return (name || '??').split(' ').filter(Boolean).slice(0, 2).map(p => p[0]).join('').toUpperCase()
}

/* ─── Coordinator Management Tab ──────────────────────── */
const COORD_COLS = ['#', 'Name', 'Phone', 'Email', 'Actions']

const CoordinatorManagementTab = ({ fetchJson }) => {
  const [rows, setRows]             = useState([])
  const [loading, setLoading]       = useState(false)
  const [totalCount, setTotalCount] = useState(0)
  const [page, setPage]             = useState(1)
  const [pageSize, setPageSize]     = useState(10)
  const [search, setSearch]         = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const searchDebounce              = useRef(null)
  const [refreshKey, setRefreshKey] = useState(0)

  /* edit modal */
  const [editCoord, setEditCoord]   = useState(null)
  const [editForm, setEditForm]     = useState({ name: '', phone: '', email: '', agent_code: '' })
  const [editSubmitting, setEditSubmitting] = useState(false)
  const [editToast, setEditToast]   = useState(null)
  const editToastTimer              = useRef(null)

  /* add modal */
  const [addOpen, setAddOpen]         = useState(false)
  const [addForm, setAddForm]         = useState({ name: '', phone: '+91', email: '', agent_code: '' })
  const [addSubmitting, setAddSubmitting] = useState(false)
  const [addToast, setAddToast]       = useState(null)
  const addToastTimer                 = useRef(null)

  /* delete modal */
  const [deleteCoord, setDeleteCoord] = useState(null)
  const [deleting, setDeleting]       = useState(false)

  useEffect(() => {
    clearTimeout(searchDebounce.current)
    searchDebounce.current = setTimeout(() => { setDebouncedSearch(search); setPage(1) }, 350)
    return () => clearTimeout(searchDebounce.current)
  }, [search])

  useEffect(() => { setPage(1) }, [pageSize])

  useEffect(() => {
    if (!fetchJson) return
    setLoading(true)
    const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) })
    if (debouncedSearch) params.set('q', debouncedSearch)
    fetchJson(`/api/agents/agent-management/coordinators/?${params}`)
      .then(data => {
        setRows(data?.results || [])
        setTotalCount(data?.count ?? 0)
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [fetchJson, page, pageSize, debouncedSearch, refreshKey])

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize))

  const openEdit = (coord) => {
    setEditCoord(coord)
    setEditForm({ name: coord.name || '', phone: coord.phone || '+91', email: coord.email || '', agent_code: coord.agent_code || '' })
    setEditToast(null)
  }

  const showEditToast = (ok, msg) => {
    clearTimeout(editToastTimer.current)
    setEditToast({ ok, msg })
    editToastTimer.current = setTimeout(() => setEditToast(null), 4000)
  }

  const showAddToast = (ok, msg) => {
    clearTimeout(addToastTimer.current)
    setAddToast({ ok, msg })
    addToastTimer.current = setTimeout(() => setAddToast(null), 4000)
  }

  const handleAddSubmit = async () => {
    if (!addForm.name.trim())  return showAddToast(false, 'Name is required.')
    if (!addForm.email.trim()) return showAddToast(false, 'Email is required.')
    if (!isValidPhone(addForm.phone)) return showAddToast(false, 'Enter a valid 10-digit phone number.')
    setAddSubmitting(true)
    try {
      await fetchJson('/api/agents/agent-management/add-coordinator/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: addForm.name, phone: addForm.phone, email: addForm.email, agent_code: addForm.agent_code }),
      })
      showAddToast(true, 'Coordinator added successfully.')
      setRefreshKey(k => k + 1)
      setTimeout(() => { setAddOpen(false); setAddForm({ name: '', phone: '', email: '', agent_code: '' }) }, 1200)
    } catch (err) {
      showAddToast(false, err?.message || 'Failed to add coordinator.')
    } finally {
      setAddSubmitting(false)
    }
  }

  const handleEditSubmit = async () => {
    if (!editForm.name.trim())  return showEditToast(false, 'Name is required.')
    if (!editForm.email.trim()) return showEditToast(false, 'Email is required.')
    if (!isValidPhone(editForm.phone)) return showEditToast(false, 'Enter a valid 10-digit phone number.')
    setEditSubmitting(true)
    try {
      await fetchJson(`/api/agents/agent-management/edit-coordinator/${editCoord.id}/`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(Object.fromEntries(
          Object.entries({ name: editForm.name, phone: editForm.phone, email: editForm.email, agent_code: editForm.agent_code })
            .filter(([k, v]) => v !== (editCoord[k] ?? ''))
        )),
      })
      showEditToast(true, 'Coordinator updated.')
      setRefreshKey(k => k + 1)
      setTimeout(() => setEditCoord(null), 1200)
    } catch (err) {
      showEditToast(false, err?.message || 'Failed to update coordinator.')
    } finally {
      setEditSubmitting(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteCoord) return
    setDeleting(true)
    try {
      await fetchJson(`/api/agents/agent-management/${deleteCoord.id}/`, { method: 'DELETE' })
      setDeleteCoord(null)
      setRefreshKey(k => k + 1)
      if (rows.length === 1 && page > 1) setPage(p => p - 1)
    } catch (err) {
      alert(err?.message || 'Failed to delete coordinator.')
    } finally {
      setDeleting(false)
    }
  }

  return (
    <>
      {/* Add modal */}
      {addOpen && (
        <>
          <div onClick={() => setAddOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.35)', zIndex: 1000 }} />
          <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', zIndex: 1001, background: '#fff', borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,.18)', width: 420, maxWidth: '90vw', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ padding: '18px 20px', borderBottom: '1px solid #E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>Add Coordinator</div>
                <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>Fill in the details to create a new coordinator</div>
              </div>
              <button onClick={() => setAddOpen(false)} style={{ background: '#F3F4F6', border: 'none', borderRadius: 6, width: 28, height: 28, cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6B7280' }}>×</button>
            </div>
            {addToast && (
              <div style={{ margin: '12px 20px 0', padding: '10px 14px', borderRadius: 8, fontSize: 13, background: addToast.ok ? '#DCFCE7' : '#FEE2E2', color: addToast.ok ? '#166534' : '#991B1B', border: `1px solid ${addToast.ok ? '#BBF7D0' : '#FECACA'}` }}>
                {addToast.msg}
              </div>
            )}
            <div style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
              {[
                { key: 'name',       label: 'Name *',       type: 'text',  placeholder: 'Full name' },
                { key: 'email',      label: 'Email *',      type: 'email', placeholder: 'email@example.com' },
              ].map(({ key, label, type, placeholder }) => (
                <div key={key}>
                  <label style={labelStyle}>{label}</label>
                  <input type={type} value={addForm[key]} onChange={e => setAddForm(f => ({ ...f, [key]: e.target.value }))} placeholder={placeholder} style={inputStyle} />
                </div>
              ))}
              <div>
                <label style={labelStyle}>Phone <span style={{ color: '#EF4444' }}>*</span></label>
                <input
                  type="text"
                  value={addForm.phone}
                  onChange={e => {
                    let v = e.target.value
                    if (!v.startsWith('+91')) v = '+91' + v.replace(/^\+91?/, '')
                    const digits = v.slice(3).replace(/\D/g, '').slice(0, 10)
                    setAddForm(f => ({ ...f, phone: '+91' + digits }))
                  }}
                  placeholder="+91XXXXXXXXXX"
                  style={{ ...inputStyle, borderColor: addForm.phone && !isValidPhone(addForm.phone) ? '#EF4444' : undefined }}
                />
              </div>
            </div>
            <div style={{ padding: '14px 20px', borderTop: '1px solid #E5E7EB', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setAddOpen(false)} style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #D1D5DB', background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#374151' }}>Cancel</button>
              <button onClick={handleAddSubmit} disabled={addSubmitting} style={{ padding: '8px 22px', borderRadius: 8, border: 'none', background: addSubmitting ? '#93C5FD' : '#2563EB', cursor: addSubmitting ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600, color: '#fff' }}>
                {addSubmitting ? 'Adding…' : 'Add Coordinator'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Edit modal */}
      {editCoord && (
        <>
          <div onClick={() => setEditCoord(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.35)', zIndex: 1000 }} />
          <div style={{
            position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
            zIndex: 1001, background: '#fff', borderRadius: 12,
            boxShadow: '0 8px 32px rgba(0,0,0,.18)', width: 420, maxWidth: '90vw',
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
          }}>
            {/* Header */}
            <div style={{ padding: '18px 20px', borderBottom: '1px solid #E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>Edit Coordinator</div>
                <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>{editCoord.agent_code}</div>
              </div>
              <button onClick={() => setEditCoord(null)} style={{ background: '#F3F4F6', border: 'none', borderRadius: 6, width: 28, height: 28, cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6B7280' }}>×</button>
            </div>
            {/* Toast */}
            {editToast && (
              <div style={{ margin: '12px 20px 0', padding: '10px 14px', borderRadius: 8, fontSize: 13, background: editToast.ok ? '#DCFCE7' : '#FEE2E2', color: editToast.ok ? '#166534' : '#991B1B', border: `1px solid ${editToast.ok ? '#BBF7D0' : '#FECACA'}` }}>
                {editToast.msg}
              </div>
            )}
            {/* Body */}
            <div style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
              {[
                { key: 'name',       label: 'Name *',       type: 'text',  placeholder: 'Full name' },
                { key: 'email',      label: 'Email *',      type: 'email', placeholder: 'email@example.com' },
              ].map(({ key, label, type, placeholder }) => (
                <div key={key}>
                  <label style={labelStyle}>{label}</label>
                  <input
                    type={type}
                    value={editForm[key]}
                    onChange={e => setEditForm(f => ({ ...f, [key]: e.target.value }))}
                    placeholder={placeholder}
                    style={inputStyle}
                  />
                </div>
              ))}
              <div>
                <label style={labelStyle}>Phone <span style={{ color: '#EF4444' }}>*</span></label>
                <input
                  type="text"
                  value={editForm.phone}
                  onChange={e => {
                    let v = e.target.value
                    if (!v.startsWith('+91')) v = '+91' + v.replace(/^\+91?/, '')
                    const digits = v.slice(3).replace(/\D/g, '').slice(0, 10)
                    setEditForm(f => ({ ...f, phone: '+91' + digits }))
                  }}
                  placeholder="+91XXXXXXXXXX"
                  style={{ ...inputStyle, borderColor: editForm.phone && !isValidPhone(editForm.phone) ? '#EF4444' : undefined }}
                />
              </div>
            </div>
            {/* Footer */}
            <div style={{ padding: '14px 20px', borderTop: '1px solid #E5E7EB', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setEditCoord(null)} style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #D1D5DB', background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#374151' }}>Cancel</button>
              <button onClick={handleEditSubmit} disabled={editSubmitting} style={{ padding: '8px 22px', borderRadius: 8, border: 'none', background: editSubmitting ? '#93C5FD' : '#2563EB', cursor: editSubmitting ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600, color: '#fff' }}>
                {editSubmitting ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Delete confirmation */}
      {deleteCoord && (
        <>
          <div onClick={() => setDeleteCoord(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', zIndex: 1000 }} />
          <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', zIndex: 1001, background: '#fff', borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,.18)', padding: '28px 28px 22px', width: 360, maxWidth: '90vw' }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#111827', marginBottom: 8 }}>Delete Coordinator?</div>
            <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 20, lineHeight: 1.5 }}>
              This will permanently delete <strong>{deleteCoord.name}</strong> ({deleteCoord.agent_code}). This action cannot be undone.
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setDeleteCoord(null)} style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #D1D5DB', background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#374151' }}>Cancel</button>
              <button onClick={handleDelete} disabled={deleting} style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: deleting ? '#FCA5A5' : '#EF4444', cursor: deleting ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600, color: '#fff' }}>
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Toolbar */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8, padding: '7px 12px', width: 300 }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search coordinators..."
              style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 13, color: '#374151', width: '100%' }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {totalCount > 0 && <span style={{ fontSize: 13, color: '#6B7280' }}>{totalCount} coordinators</span>}
            <button
              onClick={() => { setAddForm({ name: '', phone: '+91', email: '', agent_code: '' }); setAddToast(null); setAddOpen(true) }}
              style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#2563EB', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
              onMouseEnter={e => e.currentTarget.style.background = '#1D4ED8'}
              onMouseLeave={e => e.currentTarget.style.background = '#2563EB'}
            >
              <span style={{ fontSize: 16, lineHeight: 1 }}>+</span>
              Add Coordinator
            </button>
          </div>
        </div>

        {/* Table */}
        <div style={{ border: '1px solid #E5E7EB', borderRadius: 10, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
                {COORD_COLS.map(col => (
                  <th key={col} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={COORD_COLS.length} style={{ padding: '48px 0', textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={COORD_COLS.length} style={{ padding: '48px 0', textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>No coordinators found</td></tr>
              ) : rows.map((row, idx) => {
                const bg = coordAvatarColor(row.name)
                return (
                  <tr key={row.id}
                    onMouseEnter={e => e.currentTarget.style.background = '#F9FAFB'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}
                  >
                    <td style={{ padding: '10px 14px', borderBottom: '1px solid #F3F4F6', color: '#9CA3AF', fontSize: 13, whiteSpace: 'nowrap' }}>
                      {(page - 1) * pageSize + idx + 1}
                    </td>
                    <td style={{ padding: '10px 14px', borderBottom: '1px solid #F3F4F6', whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 34, height: 34, borderRadius: '50%', background: bg, color: '#fff', fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          {coordInitials(row.name)}
                        </div>
                        <span style={{ fontWeight: 600, color: '#111827', fontSize: 13 }}>{row.name}</span>
                      </div>
                    </td>
                    <td style={{ padding: '10px 14px', borderBottom: '1px solid #F3F4F6', color: '#374151', fontSize: 13, whiteSpace: 'nowrap' }}>{row.phone || <span style={{ color: '#D1D5DB' }}>—</span>}</td>
                    <td style={{ padding: '10px 14px', borderBottom: '1px solid #F3F4F6', color: '#374151', fontSize: 13 }}>{row.email || <span style={{ color: '#D1D5DB' }}>—</span>}</td>
                    <td style={{ padding: '8px 14px', borderBottom: '1px solid #F3F4F6', whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button
                          onClick={() => openEdit(row)}
                          style={{ padding: '5px 12px', border: '1px solid #BFDBFE', borderRadius: 6, background: '#EFF6FF', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#2563EB' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#DBEAFE'}
                          onMouseLeave={e => e.currentTarget.style.background = '#EFF6FF'}
                        >Edit</button>
                        <button
                          onClick={() => setDeleteCoord(row)}
                          style={{ padding: '5px 12px', border: '1px solid #FECACA', borderRadius: 6, background: '#FEF2F2', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#EF4444' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#FEE2E2'}
                          onMouseLeave={e => e.currentTarget.style.background = '#FEF2F2'}
                        >Delete</button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="t360-pagination">
          {totalPages > 1 && (
            <>
              <button className="t360-page-btn" onClick={() => setPage(p => p - 1)} disabled={page === 1 || loading}>← Prev</button>
              {buildPageNumbers(page, totalPages).map((p, i) =>
                p === '...' ? (
                  <span key={`el-${i}`} className="t360-page-ellipsis">…</span>
                ) : (
                  <button key={p} className={`t360-page-btn${p === page ? ' active' : ''}`} onClick={() => setPage(p)} disabled={loading}>{p}</button>
                )
              )}
              <button className="t360-page-btn" onClick={() => setPage(p => p + 1)} disabled={page === totalPages || loading}>Next →</button>
              <span className="t360-page-info">Page {page} of {totalPages}</span>
            </>
          )}
          <span style={{ fontSize: 12, color: '#6B7280', whiteSpace: 'nowrap' }}>
            Total: <strong style={{ color: '#111827' }}>{totalCount}</strong> coordinator{totalCount !== 1 ? 's' : ''}
          </span>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 12, color: '#6B7280', whiteSpace: 'nowrap' }}>Rows per page:</span>
            <div className="t360-select-wrap" style={{ minWidth: 70 }}>
              <select className="t360-select" value={pageSize} onChange={e => setPageSize(Number(e.target.value))} disabled={loading}>
                {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
              <span className="t360-select-arrow">▼</span>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

/* ─── Settings Page ────────────────────────────────────── */
const SettingsPage = ({ fetchJson }) => {
  const [activeTab, setActiveTab] = useState('issue_management')

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 1400 }}>
      <div>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#111827' }}>Settings</h2>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6B7280' }}>Admin-only configuration panel</p>
      </div>

      <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,.05)' }}>
        <div style={{ display: 'flex', borderBottom: '1px solid #E5E7EB', padding: '0 20px', gap: 4 }}>
          {TABS.map(tab => {
            const isActive = activeTab === tab.id
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
                padding: '14px 16px', background: 'none', border: 'none', cursor: 'pointer',
                fontSize: 13, fontWeight: isActive ? 600 : 400,
                color: isActive ? '#2563EB' : '#6B7280',
                borderBottom: isActive ? '2px solid #2563EB' : '2px solid transparent',
                marginBottom: -1, transition: 'color .15s, border-color .15s', whiteSpace: 'nowrap',
              }}
                onMouseEnter={e => { if (!isActive) e.currentTarget.style.color = '#374151' }}
                onMouseLeave={e => { if (!isActive) e.currentTarget.style.color = '#6B7280' }}
              >
                {tab.label}
              </button>
            )
          })}
        </div>
        <div style={{ padding: 20 }}>
          {activeTab === 'issue_management'       && <IssueManagementTab fetchJson={fetchJson} />}
          {activeTab === 'user_access'            && <UserManagementPage fetchJson={fetchJson} />}
          {activeTab === 'coordinator_management' && <CoordinatorManagementTab fetchJson={fetchJson} />}
        </div>
      </div>
    </div>
  )
}

export default SettingsPage
